import {
  Button,
  cn,
  GradientText,
  Icon,
  IconButton,
  LazyImage,
  Menu,
  MenuItem,
  MenuPopover,
  MenuTrigger,
  ModalDialog,
  ModalTrigger,
  ProgressBar,
  Text,
} from "@archery-inc/design-system";
import { AnimatePresence, motion } from "framer-motion";
import { Fragment, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  AiAnimatedElementGenerationTask,
  AiAnimatedElementsGeneration,
  RevealsModelName,
  TaskStatus,
} from "../__generated__/graphql";
import { getFileUrl, useFileUrl } from "../api/firebaseStorage";
import {
  downloadGeneration,
  GenerationStatus,
  getAspectRatioString,
  getGenerationStatus,
  isGenerationProcessing,
  useCancelGeneration,
  useCreateGeneration,
  useDeleteGeneration,
  useImagePosition,
} from "../api/graphql/generation";
import { amplitude } from "../api/third-party";
import { useMainStore } from "./store";

function getAnimation(fromY: string | number) {
  return {
    initial: { y: fromY, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: fromY, opacity: 0 },
    transition: { type: "spring", mass: 0.3, stiffness: 150, damping: 20 },
  };
}

export default function AiGeneration({
  data: generation,
}: {
  data: AiAnimatedElementsGeneration;
}) {
  const preset = generation.presets[0];
  const status = getGenerationStatus(generation);
  const completeMenus: MenuItemType[] = ["download", "delete"];
  const statusToMenuItems: Record<GenerationStatus, MenuItemType[]> = {
    [GenerationStatus.Canceled]: ["retry", "delete"],
    [GenerationStatus.AudioProcessing]: completeMenus,
    [GenerationStatus.AudioQueued]: completeMenus,
    [GenerationStatus.Complete]: completeMenus,
    [GenerationStatus.Error]: ["retry", "delete"],
    [GenerationStatus.Preparing]: ["cancel"],
    [GenerationStatus.Queued]: ["cancel"],
    [GenerationStatus.Generating]: ["cancel"],
  };

  const title = useMemo(() => {
    switch (status) {
      case GenerationStatus.Preparing:
        return <FormattedMessage id="ai_reveals.generation_preparing_title" />;
      case GenerationStatus.Generating:
        return <FormattedMessage id="ai_reveal_processing_title" />;
      case GenerationStatus.Queued:
        return <FormattedMessage id="ai_reveal_queued_title" />;
      default:
        return null;
    }
  }, [status]);

  const subtitle = useMemo(() => {
    switch (status) {
      case GenerationStatus.Preparing:
        return (
          <FormattedMessage id="ai_reveals.generation_preparing_description" />
        );
      case GenerationStatus.Generating:
        return <FormattedMessage id="ai_reveal_processing_subtitle" />;
      case GenerationStatus.Queued:
        return <FormattedMessage id="ai_reveal_queued_subtitle" />;
      default:
        return null;
    }
  }, [status]);

  const aspectRatio = getAspectRatioString(preset.aspectRatio);
  const isProcessing = isGenerationProcessing(generation);

  return (
    <motion.div
      style={{ aspectRatio }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ type: "spring", damping: 16 }}
      className="bg-grey-3 rounded-2 overflow-hidden relative"
      aria-label={"Generation " + generation.generationId}
    >
      <AnimatePresence>
        {[
          GenerationStatus.Complete,
          GenerationStatus.AudioProcessing,
          GenerationStatus.AudioQueued,
        ].includes(status) ? (
          <CompleteBackground
            generation={generation}
            aspectRatio={aspectRatio}
          />
        ) : (
          <IncompleteBackground
            generation={generation}
            status={status}
            aspectRatio={aspectRatio}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {title && subtitle && (
          <motion.div
            {...getAnimation("100%")}
            className="absolute bottom-0 left-0 h-32 right-0 px-5 flex flex-col overflow-hidden"
          >
            <AnimatePresence>
              <Text
                {...getAnimation(-20)}
                className="absolute top-0 left-5 right-5 text-white text-xl font-semibold whitespace-nowrap"
                key={status}
              >
                {title}
              </Text>
            </AnimatePresence>
            <AnimatePresence>
              <Text
                {...getAnimation(20)}
                className="absolute top-7 left-5 right-5 text-white/80 text-3xs whitespace-nowrap"
                key={status}
              >
                {subtitle}
              </Text>
            </AnimatePresence>
            <AnimatePresence>
              {status === GenerationStatus.Generating ? (
                <motion.div
                  {...getAnimation("100%")}
                  className="flex flex-col absolute top-12 left-5 right-5"
                >
                  <ProgressBar progress={generation.progress} />
                  <GradientText className="text-5xl font-medium">
                    {Math.round(generation.progress)}%
                  </GradientText>
                </motion.div>
              ) : (
                <motion.span
                  {...getAnimation("100%")}
                  className="absolute top-12 left-5 right-5"
                >
                  <Icon icon="spinner" className="bg-white size-12 my-3" />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
      {isProcessing && (
        <svg
          className="absolute top-0 left-0 overflow-visible w-full h-full pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.linearGradient
            id="linear"
            x1="0%"
            y1="100%"
            x2="100%"
            y2="0%"
            animate={{
              gradientTransform: [`rotate(0)`, `rotate(360)`],
            }}
            transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
          >
            <stop offset="1%" stopColor="#FF8762" />
            <stop offset="50%" stopColor="#FFCC63" />
            <stop offset="99%" stopColor="#FF7CBB" />
          </motion.linearGradient>
          <rect
            className="stroke-[url(#linear)]"
            width="100%"
            height="100%"
            fill="none"
            rx="10"
            ry="10"
            strokeWidth="10"
            strokeLinecap="square"
          />
        </svg>
      )}
      <div className="absolute top-0 right-0 p-2">
        <MenuButton generation={generation} items={statusToMenuItems[status]} />
      </div>
    </motion.div>
  );
}

const layoutTransition = {
  type: "spring",
  stiffness: 200,
  mass: 0.3,
  damping: 20,
};

function CompleteBackground({
  generation,
  aspectRatio,
}: {
  generation: AiAnimatedElementsGeneration;
  aspectRatio: string;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const task = generation.presets[0].task;
  const audioTask = generation.presets[0].audioTask;
  const videoUrl =
    (audioTask?.status === TaskStatus.Completed
      ? audioTask?.outputVideo?.url
      : task?.outputVideo?.url) ?? "";

  const [isOpen, _setIsOpen] = useState(false);
  const setIsOpen = (value: boolean) => {
    _setIsOpen(value);
    history.replaceState(
      null,
      "",
      value ? `/generations/${generation.generationId}` : "/my-generations",
    );
  };
  const thumbnail = task?.thumbnailData?.thumbnailUrl;
  const toggleMute = useMainStore((state) => state.toggleMute);
  const isMuted = useMainStore((state) => state.isMuted);

  return (
    <>
      <motion.div
        style={{ aspectRatio }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute top-0 left-0 w-full h-full cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => setIsOpen(true)}
        aria-label={"Thumbnail " + generation.generationId}
      >
        <motion.div
          layoutId={generation.generationId}
          transition={layoutTransition}
          className="w-full h-full relative"
        >
          {thumbnail && (
            <LazyImage
              src={thumbnail}
              className="w-full h-full absolute top-0 left-0"
              thumbHash={task?.thumbnailData?.thumbHash ?? undefined}
            />
          )}
          <AnimatePresence>
            {(isHovered || !thumbnail) && <Video src={videoUrl} />}
            {isHovered && audioTask?.status === TaskStatus.Completed && (
              <IconButton
                icon={isMuted ? "sound_off" : "sound"}
                variant="round"
                color="dark"
                onPress={toggleMute}
                className="absolute bottom-2 right-2"
              />
            )}
          </AnimatePresence>
          <div className="absolute bottom-0 left-0 w-full p-2 flex justify-center">
            <AnimatePresence>
              {audioTask?.status === TaskStatus.Processing && (
                <motion.div
                  initial={{ opacity: 0, y: 100 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 100 }}
                  transition={{ type: "spring", damping: 20, delay: 0.2 }}
                  className="whitespace-nowrap flex items-center text-sm text-white bg-grey-10 px-2 py-1 gap-2 rounded-2"
                >
                  <FormattedMessage id="ai_reveals.audio_processing" />
                  <Icon icon="spinner" className="bg-white" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
      <ModalTrigger onOpenChange={setIsOpen} isOpen={isOpen}>
        <Button className="hidden" />
        <ModalDialog
          layoutId={generation.generationId}
          transition={layoutTransition}
          containerClassName="max-w-lg bg-black relative"
        >
          <div style={{ aspectRatio }}>
            <video
              className="h-full rounded-4 min-w-[500px]"
              autoPlay
              playsInline
              preload="auto"
              muted={isMuted}
              loop
              src={videoUrl}
            />
          </div>
          <IconButton
            icon="close"
            color="grey"
            className="absolute top-0 right-0 m-3"
            onPress={() => setIsOpen(false)}
          />
          <div className="bottom-3 right-3 absolute flex gap-3 items-center">
            <Button
              leftIcon="download"
              color="black"
              onPress={() => downloadGeneration(generation)}
            >
              <FormattedMessage id="share_video_download" />
            </Button>
            <div>
              <IconButton
                icon={isMuted ? "sound_off" : "sound"}
                variant="round"
                color="dark"
                onPress={toggleMute}
              />
            </div>
          </div>
        </ModalDialog>
      </ModalTrigger>
    </>
  );
}

function Video({ src, layoutId }: { src: string; layoutId?: string }) {
  const [loading, setLoading] = useState(true);
  const isMuted = useMainStore((state) => state.isMuted);
  return (
    <>
      {loading && (
        <motion.div
          {...getAnimation(0)}
          className="pointer-events-none absolute top-0 left-0 w-full h-full bg-black/50"
        >
          <Icon
            icon="spinner"
            className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-16 bg-white"
          />
        </motion.div>
      )}
      <motion.video
        {...getAnimation(0)}
        src={src}
        layoutId={layoutId}
        data-testid={"video " + src}
        onLoadedMetadata={() => setLoading(false)}
        loop
        autoPlay
        playsInline
        preload="auto"
        muted={isMuted}
        className="w-full h-full absolute top-0 left-0"
      />
    </>
  );
}

function IncompleteBackground({
  generation,
  status,
  aspectRatio,
}: {
  generation: AiAnimatedElementsGeneration;
  status: GenerationStatus;
  aspectRatio: string;
}) {
  const imgData = generation.inputBucketInfo;
  const preset = generation.presets[0];
  const imgSrc = useFileUrl(imgData?.bucketName, imgData?.filePath);
  const position = useImagePosition(
    preset.imagePosition,
    preset.margin!,
    preset.coordinates?.x,
    preset.coordinates?.y,
  );
  const isProcessing = isGenerationProcessing(generation);
  const previewUrl = preset.task?.intermediateResults[0]?.url;
  return (
    <>
      <AnimatePresence initial={false}>
        {!previewUrl && (
          <motion.div
            {...getAnimation(0)}
            initial={false}
            ref={position.ref}
            style={{ aspectRatio }}
            className={cn(
              "absolute w-full h-full transition-colors duration-500",
              isProcessing ? "bg-grey-10/70" : "bg-grey-3",
            )}
          >
            <img
              onLoad={position.onImageLoad}
              src={imgSrc ?? undefined}
              className="max-w-none absolute"
              style={{
                width: position.imgW ? position.imgW : undefined,
                height: position.imgH ? position.imgH : undefined,
                left: !position.width ? undefined : position.x * position.width,
                top: !position.height
                  ? undefined
                  : position.y * position.height,
              }}
            />
            {isProcessing && (
              <div className="absolute top-0 left-0 w-full h-full bg-black-0/50" />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {previewUrl && (
          <motion.div
            {...getAnimation("100%")}
            className="absolute w-full h-full bg-grey-10/70"
          >
            <img
              src={previewUrl}
              className="absolute top-0 left-0 w-full h-full max-w-none"
            />
            {isProcessing && (
              <div className="absolute top-0 left-0 w-full h-full bg-black-0/50" />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {status === GenerationStatus.Canceled && (
          <motion.span
            className="absolute bottom-0 left-0 right-0 text-center p-2 m-3 rounded-3 bg-white"
            {...getAnimation("100%")}
          >
            <Text className="text-xs">
              <FormattedMessage id="ai_reveal_generation_canceled" />
            </Text>
          </motion.span>
        )}
      </AnimatePresence>
      <AnimatePresence initial={false}>
        {status === GenerationStatus.Error && (
          <motion.div
            {...getAnimation("100%")}
            className="absolute w-full h-full bg-grey-3"
          >
            <Icon
              icon="upload_error"
              className="absolute top-1/2 left-1/2 size-24 bg-grey-6 -translate-x-1/2 -translate-y-1/2"
            />
            <span className="absolute bottom-0 left-0 right-0 text-xs leading-6 text-center p-2 m-3 rounded-3 bg-white">
              {(preset.task?.parsedResult as { error: string })?.error}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

type MenuItemType = "retry" | "download" | "cancel" | "delete";

function MenuButton({
  generation,
  items = [],
}: {
  generation: AiAnimatedElementsGeneration;
  items?: MenuItemType[];
}) {
  const preset = generation.presets[0];

  const [cancelGeneration] = useCancelGeneration(generation.generationId);
  async function cancel() {
    amplitude.logEvent("Web:Client:AiReveals:Generation:Cancel", {
      generationId: generation.generationId,
      from: "generation-list",
    });
    await cancelGeneration();
  }

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  function deleteGeneration() {
    setDeleteModalOpen(true);
  }

  const [createGeneration] = useCreateGeneration();

  async function newGeneration(
    newPreset: Partial<AiAnimatedElementGenerationTask> = {},
  ) {
    const fileUrl =
      (await getFileUrl(
        generation.inputBucketInfo!.bucketName,
        generation.inputBucketInfo!.filePath,
      )) ?? generation.inputFileUrl;

    const inputBucketInfo = {
      bucketName: generation.inputBucketInfo!.bucketName,
      filePath: generation.inputBucketInfo!.filePath,
    };

    return createGeneration({
      variables: {
        inputFileUrl: fileUrl,
        inputBucketInfo,
        presets: [
          {
            ...newPreset,
            presetId: preset.presetId,
            margin: preset.margin,
            aspectRatio: preset.aspectRatio,
            imagePosition: preset.imagePosition,
            backgroundColor: preset.backgroundColor,
            coordinates:
              preset.coordinates?.x && preset.coordinates?.y
                ? {
                    x: preset.coordinates.x,
                    y: preset.coordinates.y,
                  }
                : undefined,
            modelName:
              preset.modelName === RevealsModelName.Runway
                ? "runway"
                : "animated-controlnets",
            seeds: Array.from({ length: 1 }, () =>
              Math.floor(Math.random() * 1000000),
            ),
          },
        ],
      },
    });
  }

  function handleMenuSelection(id: string | number) {
    switch (id) {
      case "download":
        return downloadGeneration(generation);
      case "retry":
        amplitude.logEvent("Web:Client:AiReveals:Retry", {
          generationId: generation.generationId,
        });
        return newGeneration();
      case "cancel":
        return cancel();
      case "delete":
        return deleteGeneration();
    }
  }

  const menuItems: Record<MenuItemType, React.ReactElement> = {
    download: (
      <MenuItem id="download" leftIcon="download">
        <FormattedMessage id="share_video_download" />
      </MenuItem>
    ),
    cancel: (
      <MenuItem id="cancel" leftIcon="close">
        <FormattedMessage id="ai_reveal_menu_cancel" />
      </MenuItem>
    ),
    retry: (
      <MenuItem id="retry" leftIcon="reverse_left">
        <FormattedMessage id="ai_reveal_menu_retry" />
      </MenuItem>
    ),
    delete: (
      <MenuItem
        id="delete"
        leftIcon="delete"
        iconClassName="bg-red"
        className="text-red"
      >
        <FormattedMessage id="ai_reveal_delete_generation" />
      </MenuItem>
    ),
  };

  return (
    <>
      <MenuTrigger>
        <IconButton
          icon="more"
          variant="square"
          color="dark"
          aria-label={"Menu " + generation.generationId}
        />
        <MenuPopover placement="bottom left">
          <Menu onAction={(key) => void handleMenuSelection(key)}>
            {items?.map((item) => (
              <Fragment key={item}>{menuItems[item]}</Fragment>
            ))}
          </Menu>
        </MenuPopover>
      </MenuTrigger>
      <DeleteGenerationModal
        generationId={generation.generationId}
        isOpen={isDeleteModalOpen}
        onOpenChange={setDeleteModalOpen}
      />
    </>
  );
}

function DeleteGenerationModal({
  generationId,
  isOpen,
  onOpenChange,
}: {
  generationId: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) {
  const [deleteGeneration, { loading: deleteLoading }] = useDeleteGeneration({
    generationId,
  });

  return (
    <ModalTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      {/* TODO: this is to prevent a warning, we shouldn't use the modeltrigger if the modal is controlled */}
      <Button className="hidden" />
      <ModalDialog
        className="flex flex-col gap-3 p-6"
        containerClassName="w-full max-w-lg relative"
      >
        <IconButton
          icon="close"
          color="grey"
          onPress={() => void onOpenChange(false)}
          className="absolute top-2 right-2"
        />
        <Text className="text-center">
          <FormattedMessage id="ai_reveals_delete_confirmation_message" />
        </Text>
        <div className="flex items-center justify-center mt-2 gap-4">
          <Button onPress={() => onOpenChange(false)} color="grey">
            <FormattedMessage id="common_cancel" />
          </Button>
          <Button
            onPress={() => void deleteGeneration()}
            color="red"
            isDisabled={deleteLoading}
            leftIcon={deleteLoading ? "spinner" : undefined}
            autoFocus
          >
            <FormattedMessage id="ai_reveal_delete_generation" />
          </Button>
        </div>
      </ModalDialog>
    </ModalTrigger>
  );
}
